/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { useCallback, useState } from 'react';

import CommandIntelligenceAttachModal from '../attach-modal/CommandIntelligenceAttachModal.jsx';
import { defaultGetItemKey } from '../useCommandIntelligenceSession.js';
import { getCommandIntelligenceContextBarStyles } from './CommandIntelligenceContextBar.styles.js';
import Add from '@mui/icons-material/Add';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import { Box, Button, Chip, Typography, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

const CONTEXT_BAR_MESSAGE_IDS = {
  attachedSection: 'command_intelligence_context_bar_attached_section',
  clearAttached: 'command_intelligence_context_bar_clear_attached',
  addContext: 'command_intelligence_context_bar_add',
};

const CommandIntelligenceContextBar = ({
  attachedItems,
  availableItems,
  onAttachItems,
  onDetachItem,
  onClearAttached,
  disabled,
  getItemKey = defaultGetItemKey,
  testIdPrefix = 'attach-context',
  clearButtonTestId,
  addButtonTestId,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const styles = getCommandIntelligenceContextBarStyles(theme);
  const [attachModalOpen, setAttachModalOpen] = useState(false);
  const attachedCount = attachedItems?.length ?? 0;

  const handleConfirmAttach = useCallback(
    pickedItems => {
      if (pickedItems?.length) {
        onAttachItems?.(pickedItems);
      }

      setAttachModalOpen(false);
    },
    [onAttachItems]
  );

  return (
    <Box sx={styles.root}>
      <Box sx={styles.headerRow}>
        <Box sx={styles.sectionLabelRow}>
          <DescriptionOutlined sx={styles.sectionIcon} aria-hidden />
          <Typography component="p" sx={styles.sectionLabel}>
            {intl.formatMessage({ id: CONTEXT_BAR_MESSAGE_IDS.attachedSection }, { count: attachedCount })}
          </Typography>
        </Box>

        {attachedCount > 0 ? (
          <Button
            type="button"
            size="small"
            variant="text"
            onClick={onClearAttached}
            disabled={disabled}
            sx={styles.clearButton}
            data-testid={clearButtonTestId ?? `${testIdPrefix}-clear`}
          >
            {intl.formatMessage({ id: CONTEXT_BAR_MESSAGE_IDS.clearAttached })}
          </Button>
        ) : null}
      </Box>

      {attachedCount > 0 ? (
        <Box sx={styles.chipsRow}>
          {(attachedItems ?? []).map(item => {
            const key = getItemKey(item);

            return (
              <Chip
                key={key}
                label={item?.label ?? item?.id}
                size="small"
                sx={styles.chip}
                onDelete={disabled ? undefined : () => onDetachItem?.(key)}
              />
            );
          })}
        </Box>
      ) : null}

      <Button
        type="button"
        size="small"
        variant="outlined"
        disabled={disabled || !(availableItems?.length ?? 0)}
        onClick={() => setAttachModalOpen(true)}
        startIcon={<Add fontSize="small" />}
        sx={styles.addButton}
        data-testid={addButtonTestId ?? `${testIdPrefix}-add`}
      >
        {intl.formatMessage({ id: CONTEXT_BAR_MESSAGE_IDS.addContext })}
      </Button>

      <CommandIntelligenceAttachModal
        open={attachModalOpen}
        onClose={() => setAttachModalOpen(false)}
        availableItems={availableItems}
        attachedItems={attachedItems}
        onApply={handleConfirmAttach}
        disabled={disabled}
        getItemKey={getItemKey}
        testIdPrefix={testIdPrefix}
      />
    </Box>
  );
};

CommandIntelligenceContextBar.propTypes = {
  attachedItems: PropTypes.array,
  availableItems: PropTypes.array,
  onAttachItems: PropTypes.func,
  onDetachItem: PropTypes.func,
  onClearAttached: PropTypes.func,
  disabled: PropTypes.bool,
  getItemKey: PropTypes.func,
  testIdPrefix: PropTypes.string,
  clearButtonTestId: PropTypes.string,
  addButtonTestId: PropTypes.string,
};

export default CommandIntelligenceContextBar;
